import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import {
  firmService,
  invoiceTypeService,
  cityService,
} from "../services/dataService";
import { Firm, InvoiceType, City } from "../types";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import FirmModal from "../components/FirmModal";
import { turkishIncludes } from "../utils/formatters";
import "./PurchaseOrders.css";
import "./Firms.css";
import { usePermissions } from "../context/PermissionContext";

/**
 * Firmalar (müşteri kartları) — po-* dili. Ekle/düzenle FirmModal'da; burada liste,
 * özet kutuları, fatura tipi / firma tipi süzgeçleri ve sütun süzgeçleri var. Sözlük `fm-*`.
 */

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  building: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="1.5" /><path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1M10 21v-3h4v3" /></svg>,
};

/** Fatura tipi rozet rengi: e-belge mükellefleri bilgi, faturasız soluk. */
const invoiceBadge = (name?: string) => {
  const n = (name || "").toLocaleLowerCase("tr-TR");
  if (!n) return "muted";
  if (n.includes("faturas")) return "muted";
  return "info";
};
const isPerson = (f: Firm) => f.firmType === 1; // 1 = gerçek kişi, diğerleri tüzel

type InvoiceChip = "" | "none" | number;
type TypeChip = "" | "legal" | "person";

const Firms: React.FC = () => {
  const { has } = usePermissions(); // yetki matrisi
  const [firms, setFirms] = useState<Firm[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceType[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [invoiceChip, setInvoiceChip] = useState<InvoiceChip>("");
  const [typeChip, setTypeChip] = useState<TypeChip>("");
  const [showModal, setShowModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState<Firm | null>(null);

  // Alert Modal state (silme hataları)
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    loadFirms();
    loadInvoiceTypes();
    loadCities();
  }, []);

  const loadFirms = async () => {
    try {
      setLoading(true);
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Firmalar yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceTypes = async () => {
    try {
      const data = await invoiceTypeService.getInvoiceTypes();
      setInvoiceTypes(data);
    } catch (error) {
      console.error("❌ Fatura tipleri yüklenirken hata:", error);
    }
  };

  const loadCities = async () => {
    try {
      const data = await cityService.getCities();
      setCities(data);
    } catch (error) {
      console.error("❌ İller yüklenirken hata:", error);
    }
  };

  // Form durumu, il/ilçe/vergi dairesi yükleme, doğrulama ve kayıt FirmModal içinde
  const handleOpenModal = (firm?: Firm) => {
    setEditingFirm(firm ?? null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingFirm(null);
  };

  const handleDelete = async (firm: Firm) => {
    setConfirmModal({
      isOpen: true,
      message: `"${firm.firmName}" firmasını silmek istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          await firmService.deleteFirm(firm.firmId);
          loadFirms();
        } catch (error: any) {
          console.error("❌ Firma silinirken hata:", error);

          // Backend'den gelen error mesajını string'e çevir
          let errorMessage = "Firma silinirken bir hata oluştu.";
          if (error.response?.data) {
            const errorData = error.response.data;
            if (typeof errorData === "string") {
              errorMessage = errorData;
            } else if (errorData.title || errorData.detail) {
              errorMessage = errorData.title || errorData.detail;
            } else if (errorData.errors) {
              // ASP.NET Core validation errors
              const validationErrors = Object.entries(errorData.errors)
                .map(
                  ([field, messages]: [string, any]) =>
                    `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`,
                )
                .join("\n");
              errorMessage = validationErrors;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          setAlertModal({
            isOpen: true,
            message: errorMessage,
            type: "error",
          });
        }
      },
    });
  };

  // Özet kutuları ve çip sayaçları yalnız yüklü listeden
  const kpi = useMemo(() => {
    const byInvoice = new Map<number, number>();
    let none = 0;
    let person = 0;
    for (const f of firms) {
      if (isPerson(f)) person++;
      const id = f.invoiceTypeId ?? f.invoiceType?.invoiceTypeId;
      if (id == null) none++;
      else byInvoice.set(id, (byInvoice.get(id) || 0) + 1);
    }
    const nameOf = (id: number) => invoiceTypes.find((t) => t.invoiceTypeId === id)?.name || firms.find((f) => f.invoiceType?.invoiceTypeId === id)?.invoiceType?.name || "";
    let ebelge = 0;
    let nonInvoiced = 0;
    const parts: string[] = [];
    byInvoice.forEach((n, id) => {
      const name = nameOf(id);
      if (invoiceBadge(name) === "muted") nonInvoiced += n;
      else { ebelge += n; parts.push(`${n} ${name.toLocaleLowerCase("tr-TR")}`); }
    });
    return { total: firms.length, person, legal: firms.length - person, none, byInvoice, ebelge, ebelgeParts: parts.join(" · "), nonInvoiced };
  }, [firms, invoiceTypes]);

  const filteredFirms = useMemo(() => firms.filter((firm) => {
    if (invoiceChip === "none" && (firm.invoiceTypeId ?? firm.invoiceType?.invoiceTypeId) != null) return false;
    if (typeof invoiceChip === "number" && (firm.invoiceTypeId ?? firm.invoiceType?.invoiceTypeId) !== invoiceChip) return false;
    if (typeChip === "person" && !isPerson(firm)) return false;
    if (typeChip === "legal" && isPerson(firm)) return false;
    return (
      turkishIncludes(firm.firmName, searchTerm) ||
      turkishIncludes(firm.firmCode, searchTerm) ||
      turkishIncludes(firm.contactPerson || "", searchTerm) ||
      turkishIncludes(firm.phone || "", searchTerm) ||
      turkishIncludes(firm.email || "", searchTerm)
    );
  }), [firms, invoiceChip, typeChip, searchTerm]);

  const firmColDefs = useMemo(
    () => [
      { key: "code", value: (f: Firm) => f.firmCode },
      { key: "name", value: (f: Firm) => f.firmName },
      { key: "type", value: (f: Firm) => (f.firmType === 1 ? "Gerçek Kişi" : "Tüzel Kişi") },
      { key: "invoice", value: (f: Firm) => f.invoiceType?.name || "" },
      { key: "city", value: (f: Firm) => f.city?.cityName || "" },
      { key: "tax", value: (f: Firm) => (f.firmType === 1 ? f.tckn : f.taxNumber) || "" },
    ],
    [],
  );
  const { ctx: firmCf, filtered: displayFirms } = useTableColumnFilters(filteredFirms, firmColDefs);

  if (loading && firms.length === 0) {
    return <PageLoader message="Firmalar yükleniyor..." />;
  }

  const canCreate = has("firms:create");
  const canUpdate = has("firms:update");
  const canDelete = has("firms:delete");
  const filterOn = !!(searchTerm || invoiceChip !== "" || typeChip || firmCf.hasActive);
  const resetFilters = () => { setSearchTerm(""); setInvoiceChip(""); setTypeChip(""); firmCf.clearAll(); };

  return (
    <div className="po-page fm-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Tanımlamalar</span>
          <h1>Firmalar</h1>
          <p>Sipariş verdiğiniz ve fatura kestiğiniz müşteri kartları: vergi ve kimlik bilgisi, fatura tipi, yetkili ve adres.</p>
        </div>
        {canCreate && (
          <div className="po-head-actions">
            <button type="button" className="po-btn primary" onClick={() => handleOpenModal()}>{I.plus} Yeni firma</button>
          </div>
        )}
      </header>

      <section className="po-kpis">
        <div className="po-kpi navy">
          <span className="l">Firma</span><span className="v">{kpi.total}</span>
          <span className="s"><b>{kpi.legal}</b> tüzel · <b>{kpi.person}</b> gerçek kişi</span>
        </div>
        <div className="po-kpi ok">
          <span className="l">E-belge mükellefi</span><span className="v">{kpi.ebelge}</span>
          <span className="s">{kpi.ebelgeParts || "e-fatura / e-arşiv seçilmiş firma yok"}</span>
        </div>
        <div className="po-kpi">
          <span className="l">Faturasız</span><span className="v">{kpi.nonInvoiced}</span>
          <span className="s">resmi fatura kesilmeyen müşteri</span>
        </div>
        <div className="po-kpi">
          <span className="l">Fatura tipi seçilmemiş</span><span className="v">{kpi.none}</span>
          <span className="s">{kpi.none ? "kartı düzenleyip fatura tipi seçin" : "tüm kartlarda seçili"}</span>
        </div>
      </section>

      <section className={`po-card fm-card ${loading ? "dim" : ""}`}>
        <div className="po-toolbar">
          <div className="po-search">
            {I.search}
            <input placeholder="Firma adı, kodu, yetkili, telefon veya e-posta ara" aria-label="Firma ara" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && <button type="button" className="po-ib" aria-label="Aramayı temizle" onClick={() => setSearchTerm("")}>{I.close}</button>}
          </div>
          <div className="po-chipgroup" role="group" aria-label="Fatura tipi">
            <button type="button" className={invoiceChip === "" ? "on" : ""} aria-pressed={invoiceChip === ""} onClick={() => setInvoiceChip("")}>Tüm fatura tipleri</button>
            {invoiceTypes.map((t) => (
              <button type="button" key={t.invoiceTypeId} className={invoiceChip === t.invoiceTypeId ? "on" : ""} aria-pressed={invoiceChip === t.invoiceTypeId} onClick={() => setInvoiceChip(t.invoiceTypeId)}>
                {t.name}<i>{kpi.byInvoice.get(t.invoiceTypeId) || 0}</i>
              </button>
            ))}
            {kpi.none > 0 && <button type="button" className={invoiceChip === "none" ? "on" : ""} aria-pressed={invoiceChip === "none"} onClick={() => setInvoiceChip("none")}>Seçilmemiş<i>{kpi.none}</i></button>}
          </div>
          <div className="po-chipgroup" role="group" aria-label="Firma tipi">
            <button type="button" className={typeChip === "" ? "on" : ""} aria-pressed={typeChip === ""} onClick={() => setTypeChip("")}>Tüm tipler</button>
            <button type="button" className={typeChip === "legal" ? "on" : ""} aria-pressed={typeChip === "legal"} onClick={() => setTypeChip("legal")}>Tüzel<i>{kpi.legal}</i></button>
            <button type="button" className={typeChip === "person" ? "on" : ""} aria-pressed={typeChip === "person"} onClick={() => setTypeChip("person")}>Gerçek kişi<i>{kpi.person}</i></button>
          </div>
          {filterOn && <button type="button" className="po-clear" onClick={resetFilters}>{I.filterOff} Temizle</button>}
        </div>
        <div className="po-card-head">
          <span><b>{displayFirms.length}</b> firma{displayFirms.length !== firms.length && <> / {firms.length}</>}</span>
          <span className="po-hint po-hide-sm">Başlıktaki süzgeç simgesiyle sütuna göre süzebilirsiniz</span>
        </div>

        {displayFirms.length === 0 ? (
          firms.length === 0 ? (
            <div className="po-blank">
              <span className="po-blank-icon">{I.building}</span>
              <b>Henüz firma eklenmemiş</b>
              Sipariş ve fatura için önce müşteri kartlarını tanımlayın.
              {canCreate && <button type="button" className="po-btn primary" onClick={() => handleOpenModal()}>{I.plus} İlk firmayı ekle</button>}
            </div>
          ) : (
            <div className="po-blank">
              <span className="po-blank-icon">{I.building}</span>
              <b>Süzgeçle eşleşen firma yok</b>
              Aramayı ya da süzgeçleri değiştirin.
              <button type="button" className="po-btn" onClick={resetFilters}>Süzgeçleri sıfırla</button>
            </div>
          )
        ) : (
          <div className="po-table-wrap">
            <table className="po-table">
              <thead>
                <tr>
                  <FilterTh label="Kod" colKey="code" ctx={firmCf} className="po-hide-sm" />
                  <FilterTh label="Firma" colKey="name" ctx={firmCf} />
                  <FilterTh label="Tip" colKey="type" ctx={firmCf} className="po-hide-md" />
                  <FilterTh label="Fatura tipi" colKey="invoice" ctx={firmCf} className="po-hide-sm" />
                  <FilterTh label="İl / İlçe" colKey="city" ctx={firmCf} className="po-hide-lg" />
                  <FilterTh label="Vergi no / TCKN" colKey="tax" ctx={firmCf} className="po-hide-md" />
                  <th className="act"><span className="fm-sr">İşlemler</span></th>
                </tr>
              </thead>
              <tbody>
                {displayFirms.map((firm) => {
                  const person = isPerson(firm);
                  const contact = [firm.contactPerson, firm.phone].filter(Boolean).join(" · ");
                  return (
                    <tr key={firm.firmId} className="static">
                      <td className="po-hide-sm"><span className="po-codepill fm-code">{firm.firmCode}</span></td>
                      <td>
                        <div className="po-name fm-name">{firm.firmName}</div>
                        {contact && <div className="po-sub">{contact}</div>}
                        {firm.description && <div className="po-sub fm-desc">{firm.description}</div>}
                        <div className="po-sub po-only-sm">
                          {firm.firmCode}{firm.invoiceType ? ` · ${firm.invoiceType.name}` : ""}
                        </div>
                      </td>
                      <td className="po-hide-md">
                        <span className={`po-badge ${person ? "info" : "muted"}`}>{person ? "Gerçek kişi" : "Tüzel"}</span>
                      </td>
                      <td className="po-hide-sm">
                        {firm.invoiceType ? (
                          <span className={`po-badge ${invoiceBadge(firm.invoiceType.name)}`} title={firm.invoiceType.description || undefined}>{firm.invoiceType.name}</span>
                        ) : <span className="po-sub">—</span>}
                      </td>
                      <td className="po-hide-lg">
                        {firm.city ? (
                          <>
                            <div>{firm.city.cityName}</div>
                            {firm.district && <div className="po-sub">{firm.district.districtName}</div>}
                          </>
                        ) : <span className="po-sub">—</span>}
                      </td>
                      <td className="po-hide-md">
                        {person ? (
                          <>
                            <div className="fm-mono">{firm.tckn || "—"}</div>
                            {(firm.firstName || firm.lastName) && <div className="po-sub">{firm.firstName} {firm.lastName}</div>}
                          </>
                        ) : (
                          <>
                            <div className="fm-mono">{firm.taxNumber || "—"}</div>
                            {firm.taxOffice && <div className="po-sub">{firm.taxOffice.name}</div>}
                          </>
                        )}
                      </td>
                      <td className="act">
                        <div className="po-rowacts">
                          {canUpdate && <button type="button" className="po-ib" title="Düzenle" aria-label={`${firm.firmName} düzenle`} onClick={() => handleOpenModal(firm)}>{I.edit}</button>}
                          {canDelete && <button type="button" className="po-ib danger" title="Sil" aria-label={`${firm.firmName} sil`} onClick={() => handleDelete(firm)}>{I.trash}</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Ekle / Düzenle — po-* dilinde ayrı bileşen */}
      {showModal && (
        <FirmModal
          firm={editingFirm}
          invoiceTypes={invoiceTypes}
          cities={cities}
          onClose={handleCloseModal}
          onSaved={loadFirms}
          // API e-İrsaliye alanlarını döndürüyorsa (yayınlanmış arka uç) posta kodu/etiket gösterilir; liste boşsa bilinemez, gösterilir
          despatchFields={firms.length === 0 || firms.some((x) => "despatchAlias" in x)}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Firmayı sil"
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Sil"
        cancelText="Vazgeç"
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default Firms;
